import apiClient from './axios';

/**
 * Business KYB review.
 *
 * The endpoints existed before this client did — the dashboard simply had no
 * page calling them, which is why uploaded documents were invisible and a
 * failed liveness check had no route forward.
 */

export type KybStatus = 'unverified' | 'pending' | 'verified' | 'rejected';
export type DocumentStatus = 'pending' | 'approved' | 'rejected';
export type CheckStatus =
  | 'missing'
  | 'pending'
  | 'verified'
  | 'expired'
  | 'failed';

export interface KybDocument {
  id: string;
  kind: string;
  fileName: string | null;
  status: DocumentStatus;
  rejectionReason: string | null;
  reviewedAt: string | null;
  reviewerType: string | null;
  uploadedAt: string;
  /**
   * CAC certificates only: the registration number read off the file, and
   * how (`pdf-text` is exact, `ocr` a reading of a photo, `none` nothing).
   */
  readRegistrationNumber?: string | null;
  registrationReadSource?: string | null;
  /** Its number matched the verified registration — by machine, or by a
   *  reviewer approving it. */
  registrationMatchedAt?: string | null;
  /** The business asked a person to match it because the machine couldn't. */
  registrationReviewRequestedAt?: string | null;
}

export interface KybCheck {
  id: string;
  directorId: string;
  directorName: string;
  checkType: string;
  status: CheckStatus;
  /** `manual` means a person decided rather than a provider — the distinction
   *  the record has to preserve. */
  provider: string | null;
  verifiedAt: string | null;
  expiresAt: string | null;
  /** Doubles as the justification once a check has been overridden. */
  failureReason: string | null;
  /**
   * The selfie, on a liveness check.
   *
   * It used to be sent to Dojah and discarded, so this row said "verified"
   * and there was no face to look at — the one piece of KYB evidence a
   * reviewer could not open. Signed, and short-lived by Cloudinary's own
   * rules, so it is fetched with the checks rather than cached anywhere.
   */
  fileUrl: string | null;
}

export interface KybDetail {
  kybStatus: KybStatus;
  currentTier: number;
  storedTier: number;
  /** True when the cached tier disagrees with the derived one. Surfaced rather
   *  than hidden — the disagreement is what makes a drift bug findable. */
  tierIsStale: boolean;
  outstanding: { requirement: string; detail?: string }[];
  /** The TIN step: what was typed, and what the tax registry holds. */
  tin?: KybTin;
}

export interface KybTin {
  tin: string | null;
  /** Matched to the registry, or approved by a reviewer. */
  verifiedAt: string | null;
  /** The business asked a person to check it. */
  reviewRequested: boolean;
  rejectionReason: string | null;
  canVerify: boolean;
  /** The registry's tax ID for the business's RC number at the last
   *  check. Null when it held none. */
  registryValue: string | null;
}

export const businessKybApi = {
  async detail(businessId: string): Promise<KybDetail> {
    const res = await apiClient.get(`/admin/business-kyb/${businessId}`);
    return res.data.data;
  },

  async documents(businessId: string): Promise<KybDocument[]> {
    const res = await apiClient.get(
      `/admin/business-kyb/${businessId}/documents`,
    );
    return res.data.data;
  },

  async checks(businessId: string): Promise<KybCheck[]> {
    const res = await apiClient.get(`/admin/business-kyb/${businessId}/checks`);
    return res.data.data;
  },

  /** Fetched only when a reviewer opens a document — the access is logged. */
  async documentFile(
    businessId: string,
    documentId: string,
  ): Promise<{ url: string }> {
    const res = await apiClient.get(
      `/admin/business-kyb/${businessId}/documents/${documentId}/file`,
    );
    return res.data.data;
  },

  async reviewDocument(
    businessId: string,
    documentId: string,
    approved: boolean,
    reason?: string,
  ): Promise<void> {
    await apiClient.patch(
      `/admin/business-kyb/${businessId}/documents/${documentId}/review`,
      { approved, ...(reason ? { reason } : {}) },
    );
  },

  /** Approve or turn down a TIN the business asked a person to check. */
  async reviewTin(
    businessId: string,
    approved: boolean,
    reason?: string,
  ): Promise<void> {
    await apiClient.patch(`/admin/business-kyb/${businessId}/tin/review`, {
      approved,
      ...(reason ? { reason } : {}),
    });
  },

  /**
   * Clears or fails a director check by hand.
   *
   * Behind `business_kyb.override`, which is a different permission from
   * reading this page — overruling an identity provider is what ultimately
   * lets a business take money from strangers.
   */
  async overrideCheck(
    businessId: string,
    checkId: string,
    verified: boolean,
    reason: string,
  ): Promise<{ tier: number }> {
    const res = await apiClient.patch(
      `/admin/business-kyb/${businessId}/checks/${checkId}/override`,
      { verified, reason },
    );
    return res.data.data;
  },
};

export const CHECK_LABEL: Record<string, string> = {
  bvn: 'BVN',
  nin: 'NIN',
  liveness: 'Liveness',
  address: 'Proof of address',
  id_document: 'ID document',
};

export const DOCUMENT_LABEL: Record<string, string> = {
  cac_certificate: 'CAC certificate',
  memart: 'MEMART',
  proof_of_address: 'Proof of address',
  board_resolution: 'Board resolution',
  tin_certificate: 'TIN certificate',
};

/** Falls back to the raw key rather than hiding an unlabelled kind — a
 *  reviewer seeing `some_new_doc` can still act on it. */
export function labelFor(map: Record<string, string>, key: string): string {
  return map[key] ?? key.replace(/_/g, ' ');
}
